import {createContext} from '@codeurs/front'
import requestService from 'mithril/request/request'
import {UserModel, UserState} from 'models/UserModel'
import React, {useContext, useState} from 'react'
import {useEvent} from 'util/eventcontext'
const request = requestService(window, Promise).request

type LoginState =
	| {key: 'unauthorized'}
	| {key: 'fetching'; token: string}
	| {key: 'authorized'; token: string; user: UserModel}

type AuthContextType = {
	state: LoginState
	update: (newUser: UserModel) => void
	login: (token: string, user: UserState) => void
	logout: () => void
}

export const AuthContext = createContext<AuthContextType>(undefined)

export const AuthContextProvider: React.FC = ({children}) => {
	const event = useEvent()
	const authTokenKey = `auth_token_${event.id}`

	const [state, setState] = useState<LoginState>(() => {
		const token = localStorage.getItem(authTokenKey)
		if (!token) return {key: 'unauthorized'}

		request(`/api/auth/me`, {
			method: 'post',
			headers: {
				Authorization: `Bearer ${token}`,
				accept: 'application/json'
			}
		}).then((state: UserState) => {
			if (state) {
				setState({key: 'authorized', token, user: new UserModel(state, event)})
			} else {
				localStorage.removeItem(authTokenKey)
				setState({key: 'unauthorized'})
			}
		})

		return {key: 'fetching', token}
	})

	const update = (newUser: UserModel) => {
		if (state.key !== 'authorized') return
		if (JSON.stringify(newUser.state) === JSON.stringify(state.user.state))
			return

		setState({
			...state,
			user: newUser
		})
		request(`/api/auth/me/update`, {
			method: 'post',
			headers: {
				Authorization: `Bearer ${localStorage.getItem(authTokenKey)}`,
				accept: 'application/json'
			},
			data: newUser.state
		})
	}
	const login = (token, state) => {
		localStorage.setItem(authTokenKey, token)
		setState({key: 'authorized', token, user: new UserModel(state, event)})
	}
	const logout = () => {
		localStorage.removeItem(authTokenKey)
		setState({key: 'unauthorized'})
	}

	return (
		<AuthContext.Provider value={{state, update, login, logout}}>
			{children}
		</AuthContext.Provider>
	)
}

export const useAuth: () => AuthContextType = () => {
	return useContext(AuthContext)
}
